<script setup>
import { useDetailsReport } from '#/detailsReport/useDetailsReport'
import { inject, onMounted, ref, watch } from 'vue'
import PotentialGeneralInfo from '#/detailsReport/PotentialGeneralInfo'
import FirstSteps from '#/detailsReport/FirstSteps'
import gql from 'graphql-tag'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import { useToast } from 'vue-toast-notification'
import PotentialAnalysisV1 from '#/detailsReport/version1/PotentialAnalysisV1'
import PotentialAnalysisV2 from '#/detailsReport/version2/PotentialAnalysisV2'
const $toast = useToast({ position: 'bottom' })

const props = defineProps({ version: Number, partnerWidgetLeadId: String, potentialId: String, b2cLeadId: String, demo: Boolean })
const emit = defineEmits(['InvalidAccess'])
const { lead, potential, partnerWidgetLead, b2cLead, version, demo } = useDetailsReport()
demo.value = JSON.parse(props.demo)
version.value = Number(props.version)
const { sharedApolloClient } = useAnalyzer()
const { userDetailsLoaded, isBrowser } = inject('Shared')
const loading = ref(true)

watch(userDetailsLoaded, v => {
  if (!v && !loading.value) {
    $toast.open({ type: 'info', message: 'You do not have the required permissions to view this resource.' })
    emit('InvalidAccess')
  }
})

const PotentialFields = `
  _id,
  lat,
  long,
  hasLead,
  yearOfConstructionEndMax,
  city {
    name
    buildingRegulationsPage
  }
  parcel {
    area,
    parcelNr
    builtUpArea
    maxVolume
    currentVolume
    renovationPressure
    avgFacadeHeight
    roofStats {
      flatRoofShare
    }
    avgFloors
    geometryCoordinates {
      lat
      lon
    }
    addressPoints {
      swissZipCode
    }
  }
  buildingZones {
    squareMeters
    buildingZone {
      shortName
      zoneType
      fullFloors
      frontHeight
    }
  }
  buildings {
    address,
    town,
    renovationPressure
    buildingCategory,
    apartments,
    yearOfRenovationEnd
  }
  analysis {
    score
    potentialVolume
    potentialRatioParcelArea
    potentialFacadeBuildingHeight
    potentialRatioParcelArea
    grossFloorAreaPotential
    potentialFloors
    isBuildingZoneBased
  }
  oerebLink
  oerebPdfCdnUrl
`

async function loadDetailsReport () {
  try {
    if (props.demo) {
      const res = await sharedApolloClient.value.query({
        query: gql`
            query {
              demoPotential {
                ${PotentialFields}
              }
            }
        `
      })
      if (res && res.data && res.data.demoPotential) {
        potential.value = JSON.parse(JSON.stringify(res.data.demoPotential))
      }
    } else if (props.potentialId) {
      const res = await sharedApolloClient.value.query({
        query: gql`
            query ($potentialId: String!, $queryId: String!) {
              lead (potentialId: $potentialId, queryId: $queryId) {
                _id,
                reasonOfInterest,
                furtherInterest,
                isOwner,
                networkPartnerCategories {
                  _id,
                  label,
                  text,
                  img
                },
                networkPartners {
                  logo,
                  name,
                  addresses,
                  website,
                  email,
                  categories
                },
                customer {
                  hasActiveSubscription
                },
                potential {
                  ${PotentialFields}
                }
              }
            }
        `,
        variables: {
          potentialId: props.potentialId,
          queryId: localStorage.getItem('queryId')
        }
      })
      if (res && res.data && res.data.lead && res.data.lead.potential) {
        lead.value = JSON.parse(JSON.stringify(res.data.lead))
        potential.value = lead.value.potential
      }
    } else if (props.partnerWidgetLeadId) {
      const res = await sharedApolloClient.value.query({
        query: gql`
            query ($partnerWidgetLeadId: String!) {
              partnerWidgetLead (partnerWidgetLeadId: $partnerWidgetLeadId) {
                _id,
                potential {
                  ${PotentialFields}
                }
              }
            }
        `,
        variables: {
          partnerWidgetLeadId: props.partnerWidgetLeadId
        }
      })
      if (res && res.data && res.data.partnerWidgetLead && res.data.partnerWidgetLead.potential) {
        partnerWidgetLead.value = JSON.parse(JSON.stringify(res.data.partnerWidgetLead))
        potential.value = partnerWidgetLead.value.potential
      }
    } else if (props.b2cLeadId) {
      const res = await sharedApolloClient.value.query({
        query: gql`
            query ($b2cLeadId: String!) {
              b2cLead (b2cLeadId: $b2cLeadId) {
                _id,
                potential {
                    ${PotentialFields}
                }
                ownership
                b2cUser {
                  firstName,
                  lastName,
                  email,
                  phoneNumber
                }
              }
            }
        `,
        variables: {
          b2cLeadId: props.b2cLeadId
        }
      })
      if (res && res.data && res.data.b2cLead && res.data.b2cLead.potential) {
        b2cLead.value = JSON.parse(JSON.stringify(res.data.b2cLead))
        potential.value = b2cLead.value.potential
        console.log('POT', b2cLead.value.potential)
      }
    } else {
      emit('InvalidAccess')
    }
  } catch (e) {
    console.log(e)
    console.log(JSON.parse(JSON.stringify(e)))
  }
}

onMounted(async () => {
  await loadDetailsReport()
  loading.value = false
  if (potential.value) {
    return
  }
  if (!userDetailsLoaded.value && !props.demo) {
    $toast.open({ type: 'info', message: 'You have to log in to access this resource' })
    emit('InvalidAccess')
  } else {
    $toast.open({ type: 'info', message: 'You dont have permissions to view this resource or it does not exist.' })
    emit('InvalidAccess')
  }
})
</script>

<template lang="pug">
.leadDetailsPopupContainer
  .routerPageSize(v-if="potential && isBrowser")
    slot(name="slotOne")
    potential-general-info
    slot(name="questionnaireSlot" v-if="lead && ([undefined, null].includes(lead.isOwner) || [undefined, null].includes(lead.reasonOfInterest))")
    potential-analysis-v1(v-if="version === 1")
    potential-analysis-v2(v-else-if="version === 2")
    first-steps
      template(#contactSales)
        slot(name="ctaSlot")
    slot(name="bottomSlot")
</template>

<style lang="sass">
.leadDetailsPopupContainer
  width: 100%
  position: relative
  display: flex
  justify-content: center
  @media screen and (min-width: 1024px)
    margin-top: 50px
  @media screen and (max-width: 1023px)
    margin-top: 20px
  .mapboxgl-canvas
    position: absolute
    top: 0
    bottom: 0
    width: 100% !important
    height: 100% !important
  .bigTitle
    @media screen and (min-width: 1024px)
      font-weight: 700
      font-size: 30px
      line-height: 130%
      color: #22223C
      margin-bottom: 30px
    @media screen and (max-width: 1023px)
      font-weight: 700
      font-size: 20px
      display: flex
      align-items: center
      color: #2F3849
</style>
